import c from 'classnames';
import { Suspense, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { getFirstParagraph } from 'helpers';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import useActionPlanItem from 'services/actionPlan/actionPlanItem';
import { useAllBookmarksNonSuspense } from 'services/actionPlan/useAllBookmarks';
import {
  ActionPlanDesiredOutcome,
  ActionPlanItem,
  BookmarkedItem,
} from 'types/ActionPlan';
import { FeedbackPageType } from 'types/Feedback';
import Guide from 'types/Guide';

import { Icon, IconName, Spinfinity, Typography } from '@repo/ui';
import {
  Card,
  CardSlider,
  ContainerSizes,
  EvidenceRating,
  FAQ,
  FeedbackWidget,
  Header,
  Markdown,
  Modal,
  PageContainer,
  TextLink,
} from 'components';
import HeaderIllustration from 'components/Header/HeaderIllustration';
import { TinyPlusUpsellCard } from 'components/TinyPlusUpsellCard/TinyPlusUpsellCard';
import { GuideCard } from 'views/Guides/GuideCard/GuideCard';
import { TINY_PLUS_UPSELL_IMAGES } from 'views/Home/HomeContent/RecommendedForYou/utils/getTinyPlusUpsellCard';
import { ActionDisplayTag } from './ActionDisplayTag/ActionDisplayTag';
import {
  DesiredOutcome,
  DesiredOutcomesList,
} from './DesiredOutcomes/DesiredOutcomes';
import { Recommendations } from './Recommendations/Recommendations';
import { WytuCardAP } from './WytuCardAP/WytuCardAP';

import styles from './ActionPlanDetail.module.css';

const DetailHeader = ({ action }: { action: ActionPlanItem }) => {
  const tags: string[] = useMemo(() => {
    return action?.display_tags ? action.display_tags.split(',') : [];
  }, [action]);

  return (
    <div className={styles.headerContainer}>
      <>
        {!!tags?.length && (
          <div className={styles.tagsContainer}>
            {tags.map(tag => (
              <ActionDisplayTag name={tag} key={tag} />
            ))}
          </div>
        )}
        <Typography variant='heading-xl'>{action.display_title}</Typography>
        {!!action?.subtitle && (
          <div className={styles.subtitle}>
            <Markdown>{action.subtitle}</Markdown>
          </div>
        )}
      </>
    </div>
  );
};
const Instruction = ({
  instruction,
  icon,
}: {
  instruction: string;
  icon?: IconName;
}) => {
  return (
    <div className={styles.instructions}>
      {icon && (
        <div className='flex'>
          <Icon name={icon} size='m' className='my-auto' />
        </div>
      )}
      <div className='my-auto'>
        <Typography variant='body-s' responsive>
          {instruction}
        </Typography>
      </div>
    </div>
  );
};
const Instructions = ({ action }: { action: ActionPlanItem }) => {
  if (!action.instruction_1 && !action.instruction_2 && !action.instruction_3)
    return null;
  return (
    <Card>
      <div className={styles.instructionsContainer}>
        {!!action.instruction_1 && (
          <Instruction
            instruction={action.instruction_1}
            icon={action.instruction_1_icon as IconName}
          />
        )}
        {!!action.instruction_2 && (
          <Instruction
            instruction={action.instruction_2}
            icon={action.instruction_2_icon as IconName}
          />
        )}
        {!!action.instruction_3 && (
          <Instruction
            instruction={action.instruction_3}
            icon={action.instruction_3_icon as IconName}
          />
        )}
      </div>
    </Card>
  );
};

const RecommendationsWithBookmarks = ({
  currentKitId,
  action,
}: {
  currentKitId: string;
  action: ActionPlanItem;
}) => {
  const { data: bookmarks, isFetched } = useAllBookmarksNonSuspense();

  const actionActiveBookmarks = useMemo(() => {
    return bookmarks?.filter(
      (bookmark: BookmarkedItem) =>
        bookmark.is_bookmarked && bookmark.kit_id === currentKitId,
    );
  }, [bookmarks, currentKitId]);

  const actionId = useMemo(() => {
    if (action?.id == null) return undefined;
    const asNumber =
      typeof action.id === 'number' ? action.id : Number(action.id);
    return Number.isFinite(asNumber) ? asNumber : undefined;
  }, [action?.id]);

  return (
    <Recommendations
      recommendations={action.recommendations ?? []}
      activeBookmarks={actionActiveBookmarks}
      actionId={actionId}
      showBookmarks={isFetched}
    />
  );
};

export const ActionPlanDetail = () => {
  const { actionId } = useParams<{ actionId?: string }>();
  const { currentKit, currentKitId, currentTinyAccount } =
    useCurrentKitAndTinyAccount();
  const MAX_OUTCOMES = 3;

  const { isSuccess, data: action } = useActionPlanItem({
    kitId: currentKitId,
    actionId,
  });

  const aboutIntro = useMemo(() => {
    return getFirstParagraph(action?.about);
  }, [action?.about]);
  const outcomesToDisplay =
    action?.desired_outcomes?.slice(0, MAX_OUTCOMES) ?? [];
  const hasOutcomesWithInsightsIds = outcomesToDisplay.some(
    (outcome: ActionPlanDesiredOutcome) =>
      !!outcome.criteria && /^\d+$/.test(outcome.criteria),
  );

  const guidesToShow = useMemo(() => {
    return !action?.guides?.length
      ? []
      : action.guides.map((guide: Guide) => (
          <GuideCard key={guide.id} guide={guide} />
        ));
  }, [action?.guides]);

  if (!currentTinyAccount || !currentKitId || !actionId || !action) {
    return null;
  }
  if (currentKitId && actionId && isSuccess && !action)
    return <Navigate to={`/action-plan/${currentKitId}`} replace />;

  const isBabyResampleAction =
    action?.category === 'WhenToResample' && action?.sampling_group === 'baby';

  return action?.v2 ? (
    <>
      <Header />
      <Suspense fallback={<Spinfinity />}>
        {!!action?.cover_url?.url && (
          <HeaderIllustration
            url={action.cover_url.url}
            bgColor={action.cover_url.color}
          />
        )}
        <PageContainer
          size={ContainerSizes.MD}
          className={styles.pageContainer}
        >
          <div className='mt-4'>
            <Typography variant='heading-xl'>{`${currentTinyAccount.first_name}'s action plan`}</Typography>
          </div>
          <DetailHeader action={action} />
          <div className={styles.cardsWrapper}>
            <Instructions action={action} />

            {hasOutcomesWithInsightsIds && (
              <Card>
                <h4 className={styles.cardHeading}>
                  Why do we recommend it
                  {currentTinyAccount?.first_name
                    ? ` for ${currentTinyAccount.first_name}`
                    : ''}
                  ?
                </h4>
                {/* show up to MAX_OUTCOMES and complete list in the modal */}
                {outcomesToDisplay?.map((outcome: ActionPlanDesiredOutcome) => (
                  <DesiredOutcome
                    outcome={outcome}
                    key={outcome.id + '_' + outcome.title}
                  />
                ))}
                <div className='mt-3'>
                  <TextLink
                    to={`/action-plan/${currentKitId}/action/${actionId}/insights`}
                    label='See all related insights'
                  />
                </div>
              </Card>
            )}

            {isBabyResampleAction && (
              <TinyPlusUpsellCard
                title='Testing regularly?'
                label={<>Upgrade to Tiny+ and save 20% on kits.</>}
                imgStyles={TINY_PLUS_UPSELL_IMAGES.kit}
                kit={currentKit}
                placement='baby_action_plan'
              />
            )}
          </div>

          {!!action?.action_plan_personalization?.length && (
            <WytuCardAP action={action} />
          )}

          <Typography
            variant='heading-l'
            className={c(styles.detailHeading, 'mt-4')}
          >
            About
          </Typography>
          {action?.evidence_rating && (
            <EvidenceRating evidenceRating={action.evidence_rating} />
          )}
          <div
            className={c(styles.detailAbout, action?.evidence_rating && 'mt-2')}
          >
            <Markdown>{aboutIntro ?? ''}</Markdown>
            <Modal
              title='About this metric'
              label={<span className={styles.detailMore}>Learn more</span>}
            >
              {action.name && (
                <Typography
                  variant='heading-l'
                  className={c(styles.detailHeading, 'mt-2')}
                >
                  {action.name}
                </Typography>
              )}
              <Markdown>{action?.about ?? ''}</Markdown>
            </Modal>
          </div>

          <div className='mt-4'>
            <Typography variant='body-xs' serif className={styles.disclaimers}>
              Every product in your Action Plan is there for a reason: it
              supports something specific in your results.&nbsp;
              <TextLink
                to={
                  'https://www.tinyhealth.com/blog/why-we-removed-affiliate-commissions-from-recommendations'
                }
                label='We don’t earn commission from these links'
                target='_blank'
                rel='noopener noreferrer'
              />
              —each recommendation is science-backed, carefully vetted, and
              personalized for your microbiome health.
            </Typography>
          </div>

          <RecommendationsWithBookmarks
            currentKitId={currentKitId}
            action={action}
          />

          <div className='mb-4'>
            <FeedbackWidget
              kitId={currentKitId}
              pageType={FeedbackPageType.ACTION}
              pageId={parseInt(actionId)}
            />
          </div>

          <div className='mb-4'>
            {!!action?.faq && (
              <div className='pb-4'>
                <Typography
                  variant='heading-l'
                  className={styles.detailHeading}
                >
                  Frequently asked questions
                </Typography>
                <FAQ faq={action.faq} />
              </div>
            )}
            {!!action?.guides?.length && (
              <div>
                <div className={styles.guidesRow}>
                  <Typography
                    variant='heading-l'
                    className={styles.detailHeading}
                  >
                    Related Articles
                  </Typography>
                  <TextLink label='View all' to={`/guides`} />
                </div>
                <div className={styles.guidesGridListContainer}>
                  {guidesToShow}
                </div>
                <div className={styles.guidesSliderContainer}>
                  <CardSlider cards={guidesToShow} />
                </div>
              </div>
            )}
            <div className='mt-4'>
              <Typography
                variant='body-xs'
                serif
                className={styles.disclaimers}
              >
                While grounded in research, our recommendations aren’t a
                substitute for medical advice. Check with your practitioner
                before making changes to your diet, switching up your routine,
                or adding supplements.
              </Typography>
            </div>
          </div>
        </PageContainer>
      </Suspense>
    </>
  ) : (
    <>
      {/* SIMPLE Header */}
      <Header title={`${currentTinyAccount.first_name}’s action plan`} />
      <Suspense fallback={<Spinfinity />}>
        {!!action?.cover_url?.url && (
          <HeaderIllustration
            url={action.cover_url.url}
            bgColor={action.cover_url.color}
          />
        )}
        <PageContainer
          size={ContainerSizes.MD}
          className={styles.pageContainer}
        >
          <DetailHeader action={action} />
          <div className={styles.about}>
            <Markdown>{action.detail}</Markdown>
          </div>
          <div className='mb-2'>
            {!!action?.desired_outcomes?.length && (
              <>
                <h4 className={styles.detailHeading}>Desired Outcome</h4>
                <DesiredOutcomesList outcomes={action?.desired_outcomes} />
              </>
            )}
            <div className='mt-4'>
              <FeedbackWidget
                kitId={currentKitId}
                pageType={FeedbackPageType.ACTION}
                pageId={parseInt(actionId)}
              />
            </div>
            {!!guidesToShow?.length && (
              <>
                <div className={styles.guidesRow}>
                  <Typography
                    variant='heading-l'
                    className={styles.detailHeading}
                  >
                    Related Articles
                  </Typography>
                  <TextLink label='View all' to={`/guides`} />
                </div>
                <div className={styles.guidesGridListContainer}>
                  {guidesToShow}
                </div>
                <div className={styles.guidesSliderContainer}>
                  <CardSlider cards={guidesToShow} />
                </div>
              </>
            )}
          </div>
        </PageContainer>
      </Suspense>
    </>
  );
};
